import { useMutation } from '@tanstack/vue-query'
import { useQuasar } from 'quasar'
import { computed, inject, MaybeRef, ref, unref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import { queryKeyToUrl, ReApi, ReApiVersion, ReEndpoint } from '@/composables/query'
import { curRequestKey } from '@/keys'
import axios from '@/plugins/axios'
import enums from '@/plugins/enums'
import utils from '@/plugins/utils'
import { MutationVars } from '@/types/request'

import { useRequest } from './useRequest'

interface Report {
  status: string
}

// Uses either the default curRequest (in contexts where it is available)
// or explicit ref/queryForRefetch parameters
const useReport = ({
  purpose,
  requestRef,
  queryForRefetch,
  includeQueryParams = false,
  language,
}: {
  purpose?: string
  requestRef?: string
  queryForRefetch?: ReturnType<typeof useRequest>
  includeQueryParams?: boolean
  language?: MaybeRef<string>
} = {}) => {
  const $q = useQuasar()
  const { t } = useI18n()
  const route = useRoute()

  const isDownloading = ref(false)
  const isGenerating = ref(false)

  const lang = computed(() => {
    const langValue = unref(language)
    if (langValue) {
      return langValue
    }

    // let BE handle it
    return null
  })

  const curRequest = requestRef
    ? useRequest({
        requestRefInit: requestRef,
        startDisabled: true,
      })!
    : inject(curRequestKey)!

  const reportQueryKey = computed(() => {
    const params: Record<string, string | undefined> = { purpose }
    if (lang.value) {
      params.lang = lang.value
    }
    return [
      ReApi.VALUATION,
      ReApiVersion.CURRENT,
      ReEndpoint.REQUEST,
      curRequest.requestRef,
      'report',
      params,
    ]
  })

  const mutation = useMutation<Report, Error, MutationVars>({
    mutationKey: reportQueryKey,
  })

  const reportUrl = computed(() => {
    if (!purpose && curRequest.is.oepc) {
      if (!curRequest.data?.report_ref) return
      return utils.getDocumentUrl(curRequest.requestRef, curRequest.data?.report_ref, 'view=true')
    }

    const query: Record<string, string | undefined> = { ...(includeQueryParams ? route.query : {}), purpose }

    if (lang.value) {
      query.lang = lang.value
    }

    const queryString = Object.entries(query)
      .map(([key, value]) => (value ? `${key}=${encodeURIComponent(value)}` : ''))
      .join('&')

    return _buildReportUrl(curRequest.requestRef, queryString)
  })

  async function generate(shouldDownload: boolean) {
    isGenerating.value = true
    const dismissNotification = $q.notify({
      type: 'ongoing',
      message: t('monolith.action-buttons.report_generation_in_progress'),
    })

    try {
      await mutation.mutateAsync({
        verb: 'POST',
        queryKey: reportQueryKey.value,
      })

      // Wait for the refetch to complete
      if (curRequest) {
        await curRequest.refetch()
      } else if (queryForRefetch) {
        await queryForRefetch.refetch()
      }

      // Proceed with the next step
      if (shouldDownload) {
        await download()
      } else {
        $q.notify({
          type: enums.feedbackType.POSITIVE,
          message: t('monolith.action-buttons.report_generated'),
        })
      }
    } catch (error) {
      console.error('Report generation failed:', error)
      $q.notify({
        type: enums.feedbackType.NEGATIVE,
        message: t('monolith.action-buttons.report_generation_error'),
      })
      throw error
    } finally {
      isGenerating.value = false
      dismissNotification()
    }
  }

  async function download(filename: string = `Report - ${curRequest.requestRef}.pdf`) {
    isDownloading.value = true
    const dismissNotification = $q.notify({
      type: 'ongoing',
      message: t('monolith.action-buttons.report_download_in_progress'),
    })

    // Wait for the refetch to complete
    if (curRequest) {
      await curRequest.refetch()
    } else if (queryForRefetch) {
      await queryForRefetch.refetch()
    }

    if (!reportUrl.value) {
      console.warn('Trying to download report but it is not available for download yet.')
      return
    }

    // download first with axios so that we can handle notifications properly
    return axios
      .get(reportUrl.value, { responseType: 'arraybuffer' })
      .then((response) => {
        const content = response.data
        const file = new Blob([content], {
          type: 'application/pdf',
        })

        const contentDisposition = response.headers['content-disposition']
        if (contentDisposition) {
          const fileNameMatch =
            contentDisposition.match(/filename\*=UTF-8''([^;]+)/) ||
            contentDisposition.match(/filename="([^"]+)"/)
          if (fileNameMatch?.length === 2) filename = decodeURIComponent(fileNameMatch[1])
        }

        const fileURL = URL.createObjectURL(file)
        const link = document.createElement('a')
        link.setAttribute('href', fileURL)
        link.setAttribute('download', filename)

        document.body.appendChild(link)
        link.click()

        window.URL.revokeObjectURL(fileURL)
        document.body.removeChild(link)

        $q.notify({
          type: enums.feedbackType.POSITIVE,
          message: t('monolith.action-buttons.report_downloaded'),
        })
      })
      .catch((error) => {
        console.error(error)
        $q.notify({
          type: enums.feedbackType.NEGATIVE,
          message: t('monolith.action-buttons.report_error'),
        })
      })
      .finally(() => {
        isDownloading.value = false
        dismissNotification()
      })
  }

  function _buildReportUrl(requestRef: string, queryStr: string = ''): string {
    return (
      queryKeyToUrl([ReApi.VALUATION, ReApiVersion.CURRENT, ReEndpoint.REQUEST, requestRef, 'report']).url +
      (queryStr ? `?${queryStr}` : '')
    )
  }

  const canGenerate = computed(() => {
    return curRequest.data?.can?.generate_report
  })

  const canDownload = computed(() => {
    return curRequest.data?.can?.download_report
  })

  return {
    isBusy: mutation.isPending,
    status: mutation.isPending,
    url: reportUrl,
    generate,
    download,
    canGenerate,
    canDownload,
    isDownloading,
    isGenerating,
    isProcessing: computed(() => isDownloading.value || isGenerating.value),
  }
}

export default useReport
